/**
 * Call Groups: the Call Groups page under Call Tracking
 * - Group list, entries and the routes/numbers using each group
 * - Add group, add/edit entry, in-use, delete-confirm and info modals
 *
 * The IVR route editor's select-call-group modal keeps its strings in ./ivr.ts.
 */
export const callGroupsEn = {
  callGroups: {
    title: 'Call Groups',
    searchPlaceholder: 'Search groups...',
    sidebarLabel: 'CALL GROUPS',
    memberCount: '{count} member | {count} members',
    noGroupsFound: 'No groups found',
    addGroupButton: 'Add Group',
    groupNameLabel: 'Group Name',
    groupNamePlaceholder: 'e.g., Wilshire Towers - Leasing Team',
    ivrRoutesUsingGroupHeading: 'IVR Routes Using This Group',
    noIVRRoutesMessage: 'No IVR routes are currently using this group.',
    phoneNumbersHeading: 'Phone Numbers',
    dragToReorderHint: 'Drag to reorder for Sequential dialing',
    noPhoneNumbersMessage: 'No phone numbers yet — add one to start routing calls',
    maxEntriesTooltip: 'Max 10 numbers per call group.',
    addPhoneNumberButton: 'Add phone number',
    noCallGroupsYetMessage: 'No Call Groups yet. Create a call group to start routing your calls.',
    saveSettingsButtonText: 'Save Settings',
    settingsSavedToast: 'Settings saved!',
    addCallGroupModalTitle: 'Add Call Group',
    editPhoneNumberModalTitle: 'Edit Phone Number',
    addPhoneNumberModalTitle: 'Add Phone Number',
    labelPlaceholder: 'e.g., John Smith',
    phoneNumberFieldLabel: 'Phone Number',
    phoneNumberPlaceholder: '555-123-4567',
    languagesRequiredLabel: 'Languages',
    dismissButton: 'Dismiss',
    infoIntro: 'Call groups are sets of phone numbers (operators) that can be dialed to handle calls from your IVR routes and call tracking numbers.',
    howItWorksHeading: 'How call groups work',
    howItWorksLi1: 'A <strong>call group</strong> is a list of team members reachable by phone.',
    howItWorksLi2: 'Call groups are attached to <strong>IVR routes</strong> (for example, Leasing or Resident Services), or set directly on a <strong>call tracking number</strong>.',
    howItWorksLi3: 'When a caller selects that route, or calls that number, the system <strong>dials the call group</strong> to connect them.',
    dialingBehaviorHeading: 'Dialing behavior',
    dialingBehaviorLi1: 'Operators are dialed in the <strong>order shown</strong> and you can <strong>drag & drop</strong> to reorder them.',
    dialingBehaviorLi2: 'If an operator is <strong>turned off</strong>, they are <strong>skipped</strong> during dialing.',
    whereUsedHeading: `Where they're used`,
    whereUsedLi1: 'Call groups can be <strong>shared across properties</strong>.',
    whereUsedLi2: 'If a group is only <strong>used by a single property</strong>, add the <strong>property name</strong> to the group to keep organized.',
    whereUsedLi3: '<strong>IVR Routes Using This Group</strong> — This list shows the IVR routes on your properties that this group is assigned to.',
    whereUsedLi4: '<strong>Updating a group will update it for every IVR route and call tracking number that uses it.</strong>',
    routesFields: {
      route: 'Route',
    },
    numbersUsingGroupHeading: 'Numbers Using This Group',
    noNumbersMessage: 'No call tracking numbers are currently using this group.',
    numbersFields: {
      trackingNumber: 'Tracking #',
      fallback: 'Fallback',
      fallbackValue: '{phone} ({type})',
      noProperty: 'No property',
    },
    entriesFields: {
      phoneNumber: 'Phone Number',
      languages: 'Languages',
      languagesTooltip: 'Languages this operator can answer in. For organization only; does not affect routing.',
      enabled: 'Enabled',
      enabledTooltip: 'Toggle off to skip this operator during dialing.',
    },
    deleteGroupButton: 'Delete Group',
    deleteSuccessToast: 'Call group deleted',
    deleteInUseModal: {
      title: 'This Group Is Still in Use',
      description: 'can\'t be deleted while an IVR is using it. Remove it from the routes below, then delete it.',
      hiddenRoutesDescription: 'can\'t be deleted while an IVR is using it. The routes using it are on properties you don\'t have access to.',
      openIvrSettings: 'Open IVR Settings',
      closeButton: 'Close',
      numbersDescription: 'These call tracking numbers also use this group: {numbers}.',
      hiddenNumbersOnly: '{count} call tracking number on properties you don\'t have access to also uses this group. | {count} call tracking numbers on properties you don\'t have access to also use this group.',
    },
    deleteConfirmModal: {
      title: 'Delete Call Group?',
      body: 'Are you sure you want to permanently delete \'{groupName}\' ? This action cannot be undone.',
      noRoutesNote: 'No IVR routes or call tracking numbers are using this group.',
      numbersNote: 'These call tracking numbers use this group and will go back to their forwarding number: {numbers}.',
      hiddenNumbersNote: '{count} call tracking number on properties you don\'t have access to uses this group and will go back to its forwarding number. | {count} call tracking numbers on properties you don\'t have access to use this group and will go back to their forwarding number.',
      deleteButton: 'Delete Group',
    },
    andOthers: '{list} and {count} other | {list} and {count} others',
  },
} as const;

export const callGroupsFr = {
  callGroups: {
    title: 'Groupes d\'appels',
    searchPlaceholder: 'Rechercher des groupes...',
    sidebarLabel: 'GROUPES D\'APPELS',
    memberCount: '{count} membre | {count} membres',
    noGroupsFound: 'Aucun groupe trouvé',
    addGroupButton: 'Ajouter un groupe',
    groupNameLabel: 'Nom du groupe',
    groupNamePlaceholder: 'p. ex., Tours Wilshire - Équipe de location',
    ivrRoutesUsingGroupHeading: 'Routes RVI utilisant ce groupe',
    noIVRRoutesMessage: 'Aucune route RVI n\'utilise actuellement ce groupe.',
    phoneNumbersHeading: 'Numéros de téléphone',
    dragToReorderHint: 'Faites glisser pour réorganiser la numérotation séquentielle',
    noPhoneNumbersMessage: 'Aucun numéro de téléphone pour l\'instant — ajoutez-en un pour commencer à acheminer les appels',
    maxEntriesTooltip: 'Maximum 10 numéros par groupe d\'appels.',
    addPhoneNumberButton: 'Ajouter un numéro de téléphone',
    noCallGroupsYetMessage: 'Aucun groupe d\'appels pour l\'instant. Créez un groupe d\'appels pour commencer à acheminer vos appels.',
    saveSettingsButtonText: 'Enregistrer les paramètres',
    settingsSavedToast: 'Paramètres enregistrés !',
    addCallGroupModalTitle: 'Ajouter un groupe d\'appels',
    editPhoneNumberModalTitle: 'Modifier le numéro de téléphone',
    addPhoneNumberModalTitle: 'Ajouter un numéro de téléphone',
    labelPlaceholder: 'p. ex., John Smith',
    phoneNumberFieldLabel: 'Numéro de téléphone',
    phoneNumberPlaceholder: '555-123-4567',
    languagesRequiredLabel: 'Langues',
    dismissButton: 'Ignorer',
    infoIntro: 'Les groupes d\'appels sont des ensembles de numéros de téléphone (opérateurs) qui peuvent être composés pour gérer les appels de vos routes RVI et de vos numéros de suivi des appels.',
    howItWorksHeading: 'Comment fonctionnent les groupes d\'appels',
    howItWorksLi1: 'Un <strong>groupe d\'appels</strong> est une liste de membres de l\'équipe joignables par téléphone.',
    howItWorksLi2: 'Les groupes d\'appels sont associés à des <strong>routes RVI</strong> (par exemple, Location ou Services aux résidents), ou attribués directement à un <strong>numéro de suivi des appels</strong>.',
    howItWorksLi3: 'Lorsqu\'un appelant sélectionne cette route, ou appelle ce numéro, le système <strong>compose le groupe d\'appels</strong> pour le connecter.',
    dialingBehaviorHeading: 'Comportement de numérotation',
    dialingBehaviorLi1: 'Les opérateurs sont composés dans <strong>l\'ordre affiché</strong> et vous pouvez les réorganiser par <strong>glisser-déposer</strong>.',
    dialingBehaviorLi2: 'Si un opérateur est <strong>désactivé</strong>, il est <strong>ignoré</strong> lors de la numérotation.',
    whereUsedHeading: 'Où ils sont utilisés',
    whereUsedLi1: 'Les groupes d\'appels peuvent être <strong>partagés entre les propriétés</strong>.',
    whereUsedLi2: 'Si un groupe n\'est <strong>utilisé que par une seule propriété</strong>, ajoutez le <strong>nom de la propriété</strong> au groupe pour rester organisé.',
    whereUsedLi3: '<strong>Routes RVI utilisant ce groupe</strong> — Cette liste montre les routes RVI de vos propriétés auxquelles ce groupe est assigné.',
    whereUsedLi4: '<strong>La mise à jour d\'un groupe la mettra à jour pour toutes les routes RVI et tous les numéros de suivi des appels qui l\'utilisent.</strong>',
    routesFields: {
      route: 'Route',
    },
    numbersUsingGroupHeading: 'Numéros utilisant ce groupe',
    noNumbersMessage: 'Aucun numéro de suivi des appels n\'utilise actuellement ce groupe.',
    numbersFields: {
      trackingNumber: 'N° de suivi',
      fallback: 'Secours',
      fallbackValue: '{phone} ({type})',
      noProperty: 'Aucune propriété',
    },
    entriesFields: {
      phoneNumber: 'Numéro de téléphone',
      languages: 'Langues',
      languagesTooltip: 'Langues dans lesquelles cet opérateur peut répondre. À titre organisationnel seulement; n\'affecte pas le routage.',
      enabled: 'Activé',
      enabledTooltip: 'Désactivez pour ignorer cet opérateur lors de la numérotation.',
    },
    deleteGroupButton: 'Supprimer le groupe',
    deleteSuccessToast: 'Groupe d\'appels supprimé',
    deleteInUseModal: {
      title: 'Ce groupe est encore utilisé',
      description: 'ne peut pas être supprimé tant qu\'un RVI l\'utilise. Retirez-le des routes ci-dessous, puis supprimez-le.',
      hiddenRoutesDescription: 'ne peut pas être supprimé tant qu\'un RVI l\'utilise. Les routes qui l\'utilisent se trouvent sur des propriétés auxquelles vous n\'avez pas accès.',
      openIvrSettings: 'Ouvrir les paramètres RVI',
      closeButton: 'Fermer',
      numbersDescription: 'Ces numéros de suivi des appels utilisent aussi ce groupe : {numbers}.',
      hiddenNumbersOnly: '{count} numéro de suivi des appels sur des propriétés auxquelles vous n\'avez pas accès utilise aussi ce groupe. | {count} numéros de suivi des appels sur des propriétés auxquelles vous n\'avez pas accès utilisent aussi ce groupe.',
    },
    deleteConfirmModal: {
      title: 'Supprimer le groupe d\'appels?',
      body: 'Êtes-vous sûr de vouloir supprimer définitivement \'{groupName}\' ? Cette action est irréversible.',
      noRoutesNote: 'Aucune route RVI ni aucun numéro de suivi des appels n\'utilise ce groupe.',
      numbersNote: 'Ces numéros de suivi des appels utilisent ce groupe et reviendront à leur numéro de renvoi : {numbers}.',
      hiddenNumbersNote: '{count} numéro de suivi des appels sur des propriétés auxquelles vous n\'avez pas accès utilise ce groupe et reviendra à son numéro de renvoi. | {count} numéros de suivi des appels sur des propriétés auxquelles vous n\'avez pas accès utilisent ce groupe et reviendront à leur numéro de renvoi.',
      deleteButton: 'Supprimer le groupe',
    },
    andOthers: '{list} et {count} autre | {list} et {count} autres',
  },
} as const;
